/**
 * Krisar Vastrae - Royal Curtain Page Preloader Animation
 * Handles window load completion, curtain reveal, scroll unlock, session tracking, and clean DOM removal.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'krisar_preloader_seen';
  let isInitialized = false;
  let hasRevealed = false;

  function isShopifyEditor() {
    return !!(
      (window.Shopify && window.Shopify.designMode) ||
      document.documentElement.classList.contains('shopify-design-mode')
    );
  }

  function shouldSkipPreloader(preloader) {
    if (!preloader) return true;

    const isEditor = isShopifyEditor();
    if (preloader.classList.contains('is-disabled-editor')) {
      return true;
    }

    if (isEditor) {
      return false;
    }

    const frequency = preloader.dataset.frequency || 'session';
    if (frequency === 'session') {
      try {
        if (sessionStorage.getItem(STORAGE_KEY) === 'true') {
          return true;
        }
      } catch (e) {
        return false;
      }
    } else if (frequency === 'always') {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
      return false;
    }

    return false;
  }

  function markPreloaderSeen(preloader) {
    const isEditor = isShopifyEditor();
    if (isEditor) return;

    const frequency = preloader ? (preloader.dataset.frequency || 'session') : 'session';
    if (frequency === 'session') {
      try {
        sessionStorage.setItem(STORAGE_KEY, 'true');
      } catch (e) {}
    }
  }

  function initCurtainPreloader() {
    const preloader = document.getElementById('site-preloader');
    if (!preloader) {
      document.documentElement.classList.remove('loading');
      return;
    }

    // If preloader is already animating or completed on this page, do not re-run
    if (isInitialized || preloader.dataset.animating === 'true') {
      return;
    }

    // Check if session has already seen preloader
    if (shouldSkipPreloader(preloader)) {
      preloader.style.display = 'none';
      if (preloader.parentNode && !isShopifyEditor()) {
        preloader.parentNode.removeChild(preloader);
      }
      document.documentElement.classList.remove('loading');
      return;
    }

    isInitialized = true;
    preloader.dataset.animating = 'true';
    document.documentElement.classList.add('loading');

    // Create ambient floating golden particles
    function createParticles() {
      let container = preloader.querySelector('.preloader-particles');
      if (!container) {
        container = document.createElement('div');
        container.className = 'preloader-particles';
        const centerpiece = preloader.querySelector('.curtain-centerpiece');
        if (centerpiece) {
          preloader.insertBefore(container, centerpiece);
        } else {
          preloader.appendChild(container);
        }
      }
      container.innerHTML = '';

      const isMobile = window.innerWidth <= 768;
      const count = isMobile ? 8 : 14;

      for (let i = 0; i < count; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';

        const size = (Math.random() * 3 + 2).toFixed(1);
        const left = (Math.random() * 100).toFixed(2);
        const top = (Math.random() * 100).toFixed(2);
        const duration = (Math.random() * 4 + 4).toFixed(2);
        const delay = (Math.random() * 3).toFixed(2);

        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particle.style.left = `${left}%`;
        particle.style.top = `${top}%`;
        particle.style.setProperty('--duration', `${duration}s`);
        particle.style.setProperty('--delay', `${delay}s`);

        container.appendChild(particle);
      }
    }

    createParticles();

    const durationSec = parseFloat(preloader.dataset.duration || '1.2');
    const delaySec = parseFloat(preloader.dataset.delay || '0.2');
    const durationMs = Math.round(durationSec * 1000);
    const delayMs = Math.round(delaySec * 1000);

    // Minimum visual presentation hold time
    const minHoldMs = Math.max(350, delayMs + 150);
    const startTime = Date.now();

    function revealPage() {
      if (hasRevealed) return;
      hasRevealed = true;

      const elapsed = Date.now() - startTime;
      const remainingHold = Math.max(0, minHoldMs - elapsed);

      setTimeout(function () {
        // Step 1: Smoothly fade out centerpiece logo & particles
        preloader.classList.add('is-closing');

        // Step 2: Trigger left & right curtain parting
        const openDelay = Math.max(180, delayMs);
        setTimeout(function () {
          preloader.classList.add('is-opening');

          // Step 3: Unlock scroll and allow interaction as curtains slide open
          setTimeout(function () {
            document.documentElement.classList.remove('loading');
          }, Math.min(350, Math.round(durationMs * 0.4)));

          // Step 4: After curtain slide transition fully completes, mark session as seen & clean up
          setTimeout(function () {
            markPreloaderSeen(preloader);
            preloader.style.display = 'none';

            if (!isShopifyEditor() && preloader.parentNode) {
              preloader.parentNode.removeChild(preloader);
            }
          }, durationMs + 200);
        }, openDelay);
      }, remainingHold);
    }

    // Listen for page load completion
    if (document.readyState === 'complete') {
      revealPage();
    } else {
      window.addEventListener('load', revealPage, { once: true });
    }

    // Safety fallback (max 3.5s) to guarantee curtain always opens even if third-party assets hang
    setTimeout(revealPage, 3500);
  }

  // Early initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCurtainPreloader);
  } else {
    initCurtainPreloader();
  }

  // Shopify Theme Editor live lifecycle handlers
  document.addEventListener('shopify:section:load', function (event) {
    if (
      event.target.querySelector('#site-preloader') ||
      event.target.id === 'site-preloader' ||
      event.target.classList.contains('section-preloader')
    ) {
      isInitialized = false;
      hasRevealed = false;
      initCurtainPreloader();
    }
  });

  document.addEventListener('shopify:section:select', function (event) {
    if (
      event.target.querySelector('#site-preloader') ||
      event.target.id === 'site-preloader' ||
      event.target.classList.contains('section-preloader')
    ) {
      const p = document.getElementById('site-preloader');
      if (p) {
        p.style.display = '';
        p.dataset.animating = 'false';
        p.classList.remove('is-closing', 'is-opening');
      }
      isInitialized = false;
      hasRevealed = false;
      initCurtainPreloader();
    }
  });
})();
